import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Award, 
  Users, 
  Clock, 
  Shield, 
  Star, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle,
  Target,
  Heart
} from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const AboutUs = () => {
  return (
    <>
      <SEOHead 
        title="About Us - Akiblackcar | Premium Transportation Services"
        description="Learn about Akiblackcar's commitment to excellence in luxury transportation. Over 10 years of professional service in the DMV area."
        keywords="about akiblackcar, luxury transportation, professional chauffeurs, DMV area, premium service"
      />
      
      <motion.div 
        initial="hidden" 
        animate="visible" 
        variants={staggerContainer}
        className="min-h-screen bg-gradient-to-br from-gray-900 via-brand-dark to-gray-800"
      >
        {/* Hero Section */}
        <section className="relative h-[60vh] sm:h-[70vh] min-h-[400px] sm:min-h-[500px] flex items-center justify-center text-white text-center px-4 overflow-hidden">
          {/* Background with luxury car */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-black/80 z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-gold/3 to-transparent z-15"></div>
          
          <picture>
            <source 
              media="(max-width: 768px)" 
              srcSet="https://images.unsplash.com/photo-1555215695-3004980ad54e?w=768&h=432&fit=crop&auto=format&q=80" 
              type="image/webp" 
            />
            <source 
              srcSet="https://images.unsplash.com/photo-1555215695-3004980ad54e?w=1920&h=1080&fit=crop&auto=format&q=80" 
              type="image/webp" 
            />
            <img 
              className="absolute inset-0 w-full h-full object-cover" 
              alt="Luxury black car interior" 
              src="https://images.unsplash.com/photo-1555215695-3004980ad54e?w=1920&h=1080&fit=crop&auto=format&q=80" 
              loading="eager"
            />
          </picture>
          
          <motion.div variants={fadeIn} className="relative z-20 max-w-4xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif leading-tight tracking-wider text-white drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-brand-gold via-yellow-300 to-white bg-clip-text text-transparent">
                About Akiblackcar
              </span>
            </h1>
            <div className="mt-4 mx-auto w-24 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
            <p className="mt-6 text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed drop-shadow-2xl font-medium">
              Excellence in every mile, luxury in every moment
            </p>
          </motion.div>
        </section>

        {/* Our Story Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-900 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div variants={fadeIn} className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent mb-6">
                Our Story
              </h2>
              <div className="mt-6 mx-auto w-32 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div variants={fadeIn}>
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&h=400&fit=crop&auto=format&q=80" 
                    alt="Professional chauffeur with luxury vehicle"
                    className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-lg shadow-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
                </div>
              </motion.div>

              <motion.div variants={fadeIn} className="space-y-6">
                <h3 className="text-2xl sm:text-3xl font-bold font-serif text-white">
                  Founded on Excellence
                </h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Since 2013, Akiblackcar has been the premier choice for luxury transportation in the Washington D.C. metropolitan area. What started as a vision to provide unparalleled service has grown into a trusted brand synonymous with reliability, elegance, and professionalism.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Our journey began with a simple belief: every client deserves to travel in comfort and style. Today, we continue to uphold that promise through our meticulously maintained fleet, highly trained chauffeurs, and unwavering commitment to customer satisfaction.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="flex items-center gap-2 text-brand-gold">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">10+ Years Experience</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-gold">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">1000+ Satisfied Clients</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-900 via-brand-dark to-gray-800 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div variants={fadeIn} className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent mb-6">
                Our Values
              </h2>
              <div className="mt-6 mx-auto w-32 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Target className="h-8 w-8 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Excellence
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      We strive for perfection in every aspect of our service, from vehicle maintenance to customer interaction.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Shield className="h-8 w-8 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Reliability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      Punctuality and dependability are the cornerstones of our service. You can count on us, always.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Heart className="h-8 w-8 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      We treat every client with respect and care, ensuring a personalized and memorable experience.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-900 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-gold via-yellow-400 to-transparent shadow-lg shadow-brand-gold/30"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div variants={fadeIn} className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif bg-gradient-to-r from-brand-gold via-yellow-400 to-brand-gold bg-clip-text text-transparent mb-6">
                Our Team
              </h2>
              <div className="mt-6 mx-auto w-32 h-1 bg-gradient-to-r from-transparent via-brand-gold to-transparent"></div>
              <p className="mt-8 text-lg sm:text-xl text-white leading-relaxed drop-shadow-lg max-w-3xl mx-auto">
                Meet the professionals behind Akiblackcar's exceptional service
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Users className="h-12 w-12 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Professional Chauffeurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      Our drivers are highly trained, licensed, and committed to providing safe, courteous, and professional service.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Award className="h-12 w-12 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Fleet Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      Our vehicles are meticulously maintained and regularly inspected to ensure the highest standards of safety and comfort.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 via-brand-charcoal to-gray-700 border-brand-gold/40 h-full hover:border-brand-gold/70 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-gold/20 group">
                  <CardHeader className="pb-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-brand-gold/30 to-yellow-500/30 rounded-full flex items-center justify-center group-hover:from-brand-gold/50 group-hover:to-yellow-500/50 transition-all duration-300 shadow-lg shadow-brand-gold/20">
                      <Star className="h-12 w-12 text-brand-gold group-hover:text-yellow-300 transition-colors duration-300" />
                    </div>
                    <CardTitle className="mt-6 text-xl font-serif text-white group-hover:text-brand-gold transition-colors duration-300 text-center">
                      Customer Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 group-hover:text-gray-100 transition-colors duration-300 text-center">
                      Our dedicated support team is available 24/7 to assist with bookings, changes, and any special requests.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-brand-gold via-yellow-400 to-yellow-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-brand-dark to-transparent shadow-lg shadow-brand-dark/30"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <motion.div variants={fadeIn}>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-serif text-brand-dark drop-shadow-xl mb-6">
                Ready to Experience Excellence?
              </h2>
              <div className="mt-6 mx-auto w-40 h-2 bg-gradient-to-r from-transparent via-brand-dark to-transparent shadow-lg shadow-brand-dark/30"></div>
              <p className="mt-8 text-lg sm:text-xl text-brand-dark/95 max-w-3xl mx-auto leading-relaxed font-semibold mb-12">
                Contact us today to book your premium transportation experience
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <motion.div variants={fadeIn} className="flex flex-col items-center">
                  <div className="p-4 rounded-full bg-gradient-to-br from-brand-dark/30 to-black/30 shadow-lg shadow-brand-dark/20 mb-4">
                    <Phone className="h-8 w-8 text-brand-dark" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-2">Call Us</h3>
                  <p className="text-brand-dark/80 font-medium">(703) 843-1837</p>
                </motion.div>

                <motion.div variants={fadeIn} className="flex flex-col items-center">
                  <div className="p-4 rounded-full bg-gradient-to-br from-brand-dark/30 to-black/30 shadow-lg shadow-brand-dark/20 mb-4">
                    <Mail className="h-8 w-8 text-brand-dark" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-2">Email Us</h3>
                  <p className="text-brand-dark/80 font-medium">Akiblackcar@gmail.com</p>
                </motion.div>

                <motion.div variants={fadeIn} className="flex flex-col items-center">
                  <div className="p-4 rounded-full bg-gradient-to-br from-brand-dark/30 to-black/30 shadow-lg shadow-brand-dark/20 mb-4">
                    <MapPin className="h-8 w-8 text-brand-dark" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-2">Service Area</h3>
                  <p className="text-brand-dark/80 font-medium">DMV Region</p>
                </motion.div>
              </div>

              <div className="mt-12">
                <Button asChild size="lg" className="bg-gradient-to-r from-brand-dark via-black to-brand-dark text-white hover:from-gray-700 hover:via-gray-800 hover:to-gray-700 font-bold rounded-full px-12 sm:px-16 py-6 sm:py-8 text-xl sm:text-2xl transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-brand-dark/40 border-4 border-brand-dark/30 shadow-xl shadow-brand-dark/20">
                  <Link to="/booking">Book Your Ride</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default AboutUs;
